import React, { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { useParams } from "react-router-dom";
import Skeleton from "react-loading-skeleton";
import AssignmentOutlinedIcon from "@mui/icons-material/AssignmentOutlined";
import AccessTimeOutlinedIcon from "@mui/icons-material/AccessTimeOutlined";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import ReportProblemOutlinedIcon from "@mui/icons-material/ReportProblemOutlined";
import apiClient from "../../../apiClient/index";
import { FREEZER_FROST_DASHBOARD_STATS } from "../../../apiClient/endpoints";

const statusLabel = (status) => {
  if (status === "completed") return "Completed";
  if (status === "entry_open") return "Active";
  if (status === "exit_missed" || status === "auto_closed_missing_exit") return "Exit Missed";
  return status || "—";
};

const formatWhen = (value) => {
  if (!value) return "—";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "—";
  return date.toLocaleString();
};

const formatDuration = (seconds) => {
  if (seconds == null) return "—";
  const mins = Math.round(seconds / 60);
  return `${mins} min`;
};

const FreezerFrostDashboard = () => {
  const { groupId } = useParams();
  const configData = useSelector((state) => state.configSlice);
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState(null);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      setLoading(true);
      try {
        const data = await apiClient.get(`${configData.config.API_URL}${FREEZER_FROST_DASHBOARD_STATS}/${groupId}`);
        if (!cancelled) setStats(data || {});
      } catch (error) {
        if (!cancelled) setStats({});
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    if (groupId && configData?.config?.API_URL) load();
    return () => {
      cancelled = true;
    };
  }, [groupId, configData?.config?.API_URL]);

  const cards = [
    { label: "Total Freezer Units", value: stats?.totalUnits, icon: <AssignmentOutlinedIcon />, tone: "purple" },
    { label: "Active Open Sessions", value: stats?.activeSessions, icon: <AccessTimeOutlinedIcon />, tone: "orange" },
    { label: "Completed Today", value: stats?.completedToday, icon: <CheckCircleOutlineIcon />, tone: "green" },
    { label: "Missed Exit Scan", value: stats?.missedExit, icon: <ReportProblemOutlinedIcon />, tone: "red" },
    { label: "Avg Time Inside (Today)", value: stats?.avgDurationMinutes != null ? `${stats.avgDurationMinutes} min` : "0 min", icon: <AccessTimeOutlinedIcon />, tone: "purple" },
  ];

  return (
    <div className="dashboard-v2 site-dashboard-v2">
      <div className="dashboard-v2__top">
        <div className="machines-v2__tabs dashboard-v2__tabs site-dashboard-v2__tabs">
          <button type="button" className="machines-v2__tab active">
            Freezer-Frost Dashboard
          </button>
        </div>
      </div>

      <div className="dashboard-v2__stats site-dashboard-v2__stats">
        {cards.map((card) => (
          <article className="sites-v2__stat-card dashboard-v2__mini-stat" key={card.label}>
            <div className={`sites-v2__stat-icon sites-v2__stat-icon--${card.tone === "orange" || card.tone === "red" ? "purple" : card.tone}`}>
              {card.icon}
            </div>
            <div>
              <strong>{loading ? <Skeleton width={48} /> : card.value ?? 0}</strong>
              <span>{card.label}</span>
            </div>
          </article>
        ))}
      </div>

      <div className="sites-v2__table-wrap" style={{ marginTop: 24 }}>
        <h2 className="sites-v2__title" style={{ fontSize: 18, marginBottom: 12 }}>Recent sessions</h2>
        <table className="sites-v2__table">
          <thead>
            <tr>
              <th>Freezer</th>
              <th>Entry Time</th>
              <th>Exit Time</th>
              <th>Duration</th>
              <th>Status</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan={5}>
                  <Skeleton count={3} />
                </td>
              </tr>
            ) : stats?.recentSessions?.length ? (
              stats.recentSessions.map((row) => (
                <tr key={row.uuid}>
                  <td>{row.freezerName || "—"}</td>
                  <td>{formatWhen(row.entryTime)}</td>
                  <td>{formatWhen(row.exitTime)}</td>
                  <td>{formatDuration(row.durationSeconds)}</td>
                  <td>{statusLabel(row.status)}</td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan={5}>No freezer sessions yet</td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default FreezerFrostDashboard;
